/**
 * "Doesn't add up" (trust audit Phase 3) — the client's copy of the shapes
 * the server sends (src/server/services/mismatch/read.ts, fix.ts), plus the
 * small pure rules the page and the chip share. Approved mockup:
 * https://claude.ai/artifact/FK9nvDVVgAiD4xJX1grqWJ
 */

export type MismatchOwner = "office" | "money" | "pay";

export interface MismatchProof {
    label: "FACT" | "ESTIMATE" | "MISSING";
    words: string;
    open: { label: string; href: string } | null;
}

export interface FindingView {
    id: string;
    checkId: string;
    group: string;
    owner: MismatchOwner;
    severity: "red" | "amber";
    sentence: string;
    where: string | null;
    proof: MismatchProof[];
    joiner: "but" | "and";
    fix: { label: string; kind: "run" | "open"; href: string | null; adminOk: boolean } | null;
    jobId: string | null;
    wrongSince: string | null;
    ageDays: number;
    backAgain: boolean;
    fixedAt: string | null;
    hiddenReason: string | null;
}

export interface MismatchTab {
    owner: MismatchOwner;
    who: string | null;
    count: number;
    rows: FindingView[];
    hiddenByYou: FindingView[];
}

export type MismatchList =
    | { on: false }
    | { on: true; tabs: MismatchTab[]; mine: MismatchOwner[]; fixedThisWeek: number; checkedAt: string | null };

export type MismatchChipData =
    | { on: false }
    | {
          on: true;
          count: number;
          tabs: MismatchOwner[];
          oldest: Array<{ id: string; severity: "red" | "amber"; text: string; ageDays: number; owner: MismatchOwner }>;
          checkedAt: string | null;
      };

export type UndoToken =
    | { via: "help_desk"; actionId: string }
    | { via: "quote_add_leg"; estimateId: string; assetId: string };

export type FixOutcome =
    | { kind: "done"; text: string; undo: UndoToken | null; undoUntil: string | null; stillWrong: boolean }
    | { kind: "waiting"; text: string }
    | { kind: "confirm"; text: string; preview: { title: string; lines: string[] }; confirmLength: number }
    | { kind: "open"; href: string }
    | { kind: "failed"; text: string };

export const MISMATCH_KEY = ["mismatch"] as const;
export const MISMATCH_CHIP_KEY = ["mismatch", "chip"] as const;

export const HIDE_REASONS = [
    { value: "handled_another_way", label: "Handled another way" },
    { value: "right_as_is", label: "It's right as is" },
    { value: "not_ours", label: "Not ours" },
] as const;

/** The tab's words: "Office · Darcy checks", "Regina · money", "Corbin · pay". */
export function tabWords(tab: Pick<MismatchTab, "owner" | "who">): { title: string; sub: string } {
    if (tab.owner === "office")
        return { title: "Office", sub: tab.who ? `quotes & paperwork · ${tab.who} checks` : "quotes & paperwork" };
    if (tab.owner === "money") return { title: tab.who ?? "Money", sub: "money" };
    return { title: tab.who ?? "Pay", sub: "pay" };
}

/** "4 days", "1 day", "today". */
export function ageWords(days: number): string {
    if (days <= 0) return "today";
    return days === 1 ? "1 day" : `${days} days`;
}

/** Rows grouped by kind (Quotes, Paperwork, Invoices, Points), each oldest first. */
export function groupRows(rows: FindingView[]): Array<{ group: string; rows: FindingView[] }> {
    const order = ["Quotes", "Paperwork", "Invoices", "Points", "Other"];
    const by = new Map<string, FindingView[]>();
    for (const r of rows) by.set(r.group, [...(by.get(r.group) ?? []), r]);
    return [...by.entries()]
        .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
        .map(([group, list]) => ({ group, rows: list }));
}

/** What the toolbar chip says. Gray at 0, orange at 1 or more; "—" when it couldn't load. */
export function chipState(
    data: MismatchChipData | undefined,
    isError: boolean,
): {
    show: boolean;
    n: number | null;
    hot: boolean;
    label: string;
} {
    if (isError) return { show: true, n: null, hot: false, label: "Doesn't add up — couldn't load your count" };
    if (!data || !data.on) return { show: false, n: null, hot: false, label: "" };
    const n = data.count;
    return {
        show: true,
        n,
        hot: n > 0,
        label: n > 0 ? `Doesn't add up, ${n} for you` : "Doesn't add up, nothing for you",
    };
}

/** "Checked at 5:02 AM" — the last nightly run, in the viewer's clock. */
export function checkedWords(at: string | null): string {
    if (!at) return "Not checked yet";
    const d = new Date(at);
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    const today = new Date().toDateString() === d.toDateString();
    return today
        ? `Checked today at ${time}`
        : `Checked ${d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" })} at ${time}`;
}
